import { z } from 'zod';
import {
  type RegexSiteSeparationRuleBase,
  regexSiteSeparationRuleBaseSchemaZ,
} from './regexSiteSeparationRule';
import {
  type SiteSeparationAttributeTransformation,
  siteSeparationAttributeTransformationSchemaZ,
} from './siteSeparationAttributeTransformation';
import {
  type SiteSeparationRequestRule,
  type SiteSeparationRule,
  siteSeparationRequestRuleSchemaZ,
  siteSeparationRuleSchemaZ,
} from './siteSeparationRule';

export interface SiteSeparation {
  manualEnabled: boolean; // first rule to run
  neighborshipFallbackEnabled: boolean; // last rule to run
  rules: SiteSeparationRule[];
}

export interface SiteSeparationRequest {
  manualEnabled: boolean; // first rule to run
  neighborshipFallbackEnabled: boolean; // last rule to run
  rules: SiteSeparationRequestRule[];
}

export interface TestRegexSiteSeparationRuleRequestBody
  extends RegexSiteSeparationRuleBase {
  snapshot?: string;
}

export interface TestRegexSiteSeparationCloudBasedRuleRequestBody
  extends TestRegexSiteSeparationRuleRequestBody {
  applyToCloudInstances?: boolean;
}

/**
 * Test-rule request for the `cloudResourceTag` rule: no regex, just the selected
 * tag names (first-match-wins) + transformation, mirroring the rule shape itself.
 */
export interface TestCloudResourceTagSiteSeparationRuleRequestBody {
  type: 'cloudResourceTag';
  tagNames: string[];
  transformation: SiteSeparationAttributeTransformation;
  snapshot?: string;
  applyToCloudInstances?: boolean;
}

export interface DeviceForSiteSeparation {
  hostname: string;
  sn: string;
  snmpLocation: string | null;
  cloudResourceId: string | null;
}

export interface TestRegexSiteSeparationRuleCommonResult {
  vDeviceId: string;
  hostname: string;
  sn: string;
  snmpLocation: string | null;
  cloudResourceId: string | null;
  tagValue?: string | null;
  vVirtualMachinesId?: string;
  vCloudNetworkId?: string;
}

export interface TestRegexSiteSeparationRuleMatchedResult
  extends TestRegexSiteSeparationRuleCommonResult {
  siteName: string;
}

export interface TestRegexSiteSeparationRuleUnmatchedResult
  extends TestRegexSiteSeparationRuleCommonResult {}

export interface VirtualMachineForRegexTesting {
  id: string;
  vDeviceId: string;
}

export interface CloudNetworkForRegexTesting {
  vCloudNetworkId: string;
  id: string;
  vendor: string;
  resourceName: string;
  name: string | null;
}

export interface TestRegexSiteSeparationCloudNetworkMatchedResult
  extends CloudNetworkForRegexTesting {
  siteName: string;
}

export interface TestRegexSiteSeparationCloudNetworkUnmatchedResult
  extends CloudNetworkForRegexTesting {}

/**
 * A cloud network row for the `cloudResourceTag` Test rule preview: the shared
 * network shape plus the value of the first selected tag the network carries
 * (null when it carries none, so the caller can split matched vs unmatched).
 */
export interface CloudNetworkWithTagValueForTesting
  extends CloudNetworkForRegexTesting {
  tagValue: string | null;
}

export interface TestRegexSiteSeparationRuleResponseBody {
  matched: TestRegexSiteSeparationRuleMatchedResult[];
  unmatched: TestRegexSiteSeparationRuleUnmatchedResult[];
  // Only populated when the tested rule type is 'regexCloudResourceId'.
  matchedCloudNetworks?: TestRegexSiteSeparationCloudNetworkMatchedResult[];
  unmatchedCloudNetworks?: TestRegexSiteSeparationCloudNetworkUnmatchedResult[];
}

export const siteSeparationSchemaZ = z
  .object({
    manualEnabled: z.boolean(),
    neighborshipFallbackEnabled: z.boolean(),
    rules: z.array(siteSeparationRuleSchemaZ.unwrap()).refine(
      (arr) => {
        const ids = arr.map((r) => r.id);
        return new Set(ids).size === ids.length;
      },
      {
        message: 'Must be unique',
      },
    ),
  })
  .strict();

export const siteSeparationRequestSchemaZ = z
  .object({
    manualEnabled: z.boolean(),
    neighborshipFallbackEnabled: z.boolean(),
    rules: z.array(siteSeparationRequestRuleSchemaZ.unwrap()).refine(
      (arr) => {
        const ids = arr.filter((x) => x.id !== undefined).map((r) => r.id);

        return new Set(ids).size === ids.length;
      },
      {
        message: 'Must be unique',
      },
    ),
  })
  .strict()
  .optional();

export const testRegexSiteSeparationRuleRequestBodyZ = z
  .object({
    ...regexSiteSeparationRuleBaseSchemaZ.shape,
    snapshot: z.string().nonempty().optional(),
    applyToCloudInstances: z.boolean().optional(),
  })
  .strict()
  .optional();

export const testCloudResourceTagSiteSeparationRuleRequestBodyZ = z
  .object({
    type: z.literal('cloudResourceTag'),
    tagNames: z.array(z.string().nonempty()).min(1),
    transformation: siteSeparationAttributeTransformationSchemaZ,
    snapshot: z.string().nonempty().optional(),
    applyToCloudInstances: z.boolean().optional(),
  })
  .strict();
